/** A lock as a picker deals in it: the two parts its stored path carries. */
export interface LockReference {
  readonly zoneName: string;
  readonly name: string;
}

/**
 * The stored wire form, `zone_name|name`.
 *
 * Stored as `zone_name|name`, shown as `name (zone)` — see {@link lockLabel}. Those are trivially
 * swappable, and swapping them shows the user a raw config value or writes a display string into the
 * config, so the two live side by side here rather than being inlined per control.
 */
export const formatLockPath = (lock: LockReference): string => `${lock.zoneName}|${lock.name}`;

/** How a lock reads to a user. A path that carries no zone shows as the bare name. */
export const lockLabel = (lock: LockReference): string => (lock.zoneName === '' ? lock.name : `${lock.name} (${lock.zoneName})`);

/**
 * The inverse of {@link formatLockPath}.
 *
 * A zone name holds no `|`, so the first one separates the parts and everything after it is the lock
 * name; a path without one is malformed and reads as zone-less rather than being rejected, so a
 * value the picker cannot explain still survives the round trip instead of being dropped on save.
 */
export const parseLockPath = (path: string): LockReference => {
  const separator = path.indexOf('|');
  return separator === -1 ? { zoneName: '', name: path } : { zoneName: path.slice(0, separator), name: path.slice(separator + 1) };
};
